import React, { useEffect, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { imageTypeRegistryAdminAPI } from '../../services/api';
import { toast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

type Category = { key: string; description: string };
type Version = {
  id: number;
  category_key: string;
  version: number;
  description: string;
  model_provider: string | null;
  prompt_template: string | null;
  safety_review_status: 'pending' | 'approved';
  status: 'draft' | 'live' | 'retired';
};

/**
 * 2026-09-10 (plan 09 — real page replacing Docs/mockups/
 * vision-model-registry-admin.html). Platform-admin only. The backend
 * (get_current_super_admin) is the actual enforcement — this page's own
 * role check is a UX nicety (don't show a form whose every action 403s),
 * not the security boundary.
 */
const ImageTypeRegistryAdminPage: React.FC = () => {
  const { user } = useAppSelector((state: any) => state.auth);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const [categories, setCategories] = useState<Category[]>([]);
  const [versionsByCategory, setVersionsByCategory] = useState<Record<string, Version[]>>({});
  const [loading, setLoading] = useState(true);
  const [draftForms, setDraftForms] = useState<Record<string, { description: string; model_provider: string; prompt_template: string }>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const cats: Category[] = await imageTypeRegistryAdminAPI.getCategories();
      setCategories(cats);
      const entries = await Promise.all(
        cats.map(async (c) => [c.key, await imageTypeRegistryAdminAPI.getVersions(c.key)] as const),
      );
      setVersionsByCategory(Object.fromEntries(entries));
    } catch (error: any) {
      toast({
        title: 'Could not load the registry',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) void load();
    else setLoading(false);
  }, [isSuperAdmin]);

  const emptyDraftForm = { description: '', model_provider: '', prompt_template: '' };

  const updateDraftForm = (key: string, field: string, value: string) => {
    setDraftForms((prev) => {
      const base = prev[key] || emptyDraftForm;
      return { ...prev, [key]: { ...base, [field]: value } };
    });
  };

  const createDraft = async (categoryKey: string) => {
    const form = draftForms[categoryKey];
    if (!form?.description?.trim()) {
      toast({ title: 'A description is required', variant: 'destructive' });
      return;
    }
    setSavingKey(categoryKey);
    try {
      await imageTypeRegistryAdminAPI.createDraft(categoryKey, form);
      toast({ title: 'Draft version created' });
      setDraftForms((prev) => ({ ...prev, [categoryKey]: { description: '', model_provider: '', prompt_template: '' } }));
      await load();
    } catch (error: any) {
      toast({
        title: 'Could not create draft',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setSavingKey(null);
    }
  };

  const approve = async (versionId: number) => {
    try {
      await imageTypeRegistryAdminAPI.approveSafetyReview(versionId);
      toast({ title: 'Safety review approved' });
      await load();
    } catch (error: any) {
      toast({ title: 'Could not approve', description: error?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const publish = async (versionId: number) => {
    try {
      await imageTypeRegistryAdminAPI.publishVersion(versionId);
      toast({ title: 'Version published — now live' });
      await load();
    } catch (error: any) {
      toast({ title: 'Could not publish', description: error?.response?.data?.detail, variant: 'destructive' });
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardContent className="py-10 flex flex-col items-center text-center gap-2">
            <ShieldAlert className="h-8 w-8 text-muted-foreground" />
            <p className="font-medium">Platform admin only</p>
            <p className="text-sm text-muted-foreground">
              This page manages the model/prompt behind every account's Image Types — restricted
              to platform engineering.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-4xl">
      <div>
        <div className="inline-block text-xs font-semibold tracking-wide uppercase text-muted-foreground border rounded px-2 py-1 mb-2">
          Platform admin · not visible to tenant account admins
        </div>
        <h1 className="text-2xl font-bold tracking-tight">Vision Model Registry</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Where a category gets its actual model, prompt, and safety sign-off — before it's
          eligible to appear as a toggle in any tenant's Image Types.
        </p>
      </div>

      {categories.map((cat) => {
        const versions = versionsByCategory[cat.key] || [];
        const live = versions.find((v) => v.status === 'live');
        const form = draftForms[cat.key] || { description: '', model_provider: '', prompt_template: '' };
        return (
          <Card key={cat.key}>
            <CardContent className="py-4 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <code className="text-sm font-semibold bg-muted px-2 py-0.5 rounded">{cat.key}</code>
                {live ? (
                  <Badge>Live · v{live.version}</Badge>
                ) : (
                  <Badge variant="secondary">Not started · no live version</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{cat.description}</p>

              {versions.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Versions</Label>
                  {versions.map((v) => (
                    <div key={v.id} className="border rounded p-3 space-y-1.5 text-sm">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="font-medium">v{v.version}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant={v.status === 'live' ? 'default' : v.status === 'retired' ? 'secondary' : 'outline'}>
                            {v.status}
                          </Badge>
                          <Badge variant={v.safety_review_status === 'approved' ? 'default' : 'secondary'}>
                            safety review: {v.safety_review_status}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-muted-foreground">{v.description}</p>
                      {v.model_provider && (
                        <p className="text-xs text-muted-foreground">Model: {v.model_provider}</p>
                      )}
                      {v.status === 'draft' && (
                        <div className="flex gap-2 pt-1">
                          {v.safety_review_status !== 'approved' && (
                            <Button size="sm" variant="outline" onClick={() => approve(v.id)}>
                              Approve safety review
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => publish(v.id)}
                            disabled={v.safety_review_status !== 'approved'}
                          >
                            Publish
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="border border-dashed rounded p-3 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  New draft version
                </Label>
                <Input
                  placeholder="Description shown to tenant admins"
                  value={form.description}
                  onChange={(e) => updateDraftForm(cat.key, 'description', e.target.value)}
                />
                <Input
                  placeholder="Model provider (e.g. groq_qwen_vision)"
                  value={form.model_provider}
                  onChange={(e) => updateDraftForm(cat.key, 'model_provider', e.target.value)}
                />
                <Textarea
                  placeholder="Prompt template"
                  rows={3}
                  value={form.prompt_template}
                  onChange={(e) => updateDraftForm(cat.key, 'prompt_template', e.target.value)}
                />
                <Button size="sm" onClick={() => createDraft(cat.key)} disabled={savingKey === cat.key}>
                  {savingKey === cat.key && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create draft
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default ImageTypeRegistryAdminPage;
